import { computed, signal, Signal } from '@preact/signals';

import { DebugLog } from '../debug-log';
import { logMessage } from '../sentry';
import { isFeatureEnable } from '../features-enabling';
import { BrowserStore } from './browser.store';
import {
  AdContainerExposure,
  AdContainerState,
  ErrorEvent,
  NavigationEvent
} from '../../domain/ad-container.domain';
import { IAdContainerPort } from '../../domain/ports/ad-container.port';
import { IBrowserContainer } from '../../domain/browser-container.domain';
import { sendSupplyMonitoringEvent } from '../supply-monitoring-events/supply-monitoring-events.service';
import {
  AD_DISPLAYED,
  RENDERING_ERROR,
  UNLOAD_ERROR
} from '../supply-monitoring-events/supply-monitoring-events.type';
import { sendAdsEventsTrack } from '../analytics/ads-events-track.service';

export class AdContainerStore implements IBrowserContainer {
  static MIN_PERCENTAGE_VIEWABLE = 50;

  adContainerPort: IAdContainerPort;
  msAdsOptions: FormatOptions;
  browserStore: BrowserStore;

  state: Signal<AdContainerState> = signal('default');
  isViewable: Signal<boolean> = signal(false);
  exposure: Signal<AdContainerExposure> = signal({
    percentageInView: 0,
    visibleRectangle: undefined,
    occlusionRectangles: []
  });
  isReady: Signal<boolean> = computed(() => {
    return this.state.value !== 'loading';
  });
  navigationEvent: Signal<NavigationEvent | null> = signal(null);

  constructor(
    adContainerPort: IAdContainerPort,
    msAdsOptions: FormatOptions = {} as FormatOptions
  ) {
    this.adContainerPort = adContainerPort;
    this.msAdsOptions = msAdsOptions;

    this.browserStore = new BrowserStore(this);

    this.updateState(this.adContainerPort.getState());
    this.updateIsViewable(this.adContainerPort.isViewable());

    this.initObservers();
    DebugLog.log('AdContainerStore constructor completed', {
      state: this.state.value,
      isViewable: this.isViewable.value
    });
  }

  initObservers(): void {
    DebugLog.log('AdContainerStore initObservers called');
    this.adContainerPort.onStateChange(this.updateState.bind(this));
    this.adContainerPort.onViewabilityChange(this.updateIsViewable.bind(this));

    this.adContainerPort.onExposureChange((exposure: AdContainerExposure) => {
      this.updateExposure(exposure);

      // Update viewability based on exposure percentage
      this.updateIsViewable(
        exposure.percentageInView >= AdContainerStore.MIN_PERCENTAGE_VIEWABLE
      );
    });

    this.adContainerPort.onError((error: ErrorEvent) => {
      logMessage('MRAID container error', error);
    });

    this.adContainerPort.onNavigationEvent(
      (event: NavigationEvent) => (this.navigationEvent.value = event)
    );
  }

  updateState(state: AdContainerState): void {
    DebugLog.log('AdContainerStore updateState called', {
      oldState: this.state.value,
      newState: state
    });
    if (this.state.value === state) {
      DebugLog.log('AdContainerStore state unchanged, skipping update');
      return;
    }
    this.state.value = state;
    DebugLog.log('AdContainerStore state updated', {
      state: this.state.value,
      signalValue: this.state.value
    });
  }

  openAd(url: string, forceDefaultBrowser: boolean = false): void {
    if (isFeatureEnable('oguryBrowser')) {
      this.openBrowser(url, forceDefaultBrowser);
      return;
    }
    this.adContainerPort.openAd(url, forceDefaultBrowser);
  }

  openBrowser(url: string, forceDefaultBrowser: boolean = false): void {
    this.browserStore.open(url, { forceDefault: forceDefaultBrowser });
  }

  updateIsViewable(isViewable: boolean): void {
    if (this.isViewable.value === isViewable) return;
    this.isViewable.value = isViewable;
  }

  updateExposure(exposure: AdContainerExposure): void {
    this.exposure.value = exposure;
  }

  unloadFormat(): void {
    const sendLoadedErrorPromise = sendAdsEventsTrack('loaded_error');
    const sendSupplyMonitoringEventPromise = sendSupplyMonitoringEvent(
      RENDERING_ERROR,
      {
        error_type: UNLOAD_ERROR
      }
    );

    Promise.all([
      sendLoadedErrorPromise,
      sendSupplyMonitoringEventPromise
    ]).then(() => {
      if (this.adContainerPort.getVersion() >= 3) {
        this.adContainerPort.unload();
      } else {
        this.adContainerPort.close();
      }
    });
  }

  triggerOnAdLoaded() {
    this.adContainerPort.ogyOnAdLoaded();
    sendAdsEventsTrack('loaded');
  }

  triggerOnAdImpression() {
    sendSupplyMonitoringEvent(AD_DISPLAYED);
    this.adContainerPort.ogyOnAdImpression();
  }

  canOpenStoreKit(ctx: Partial<AdContext>): boolean {
    return !!(
      (ctx.adUnitType === 'interstitial' || ctx.adUnitType === 'optin_video') &&
      ctx.skan &&
      isFeatureEnable('skan') &&
      this.adContainerPort.hasCmd('ogyOpenStoreKit')
    );
  }

  canOpenSKOverlay(ctx: { adUnitType: string; skan: boolean }): boolean {
    return (
      (ctx.adUnitType === 'interstitial' || ctx.adUnitType === 'optin_video') &&
      ctx.skan &&
      isFeatureEnable('skoverlay') &&
      this.adContainerPort.hasCmd('ogyOpenSKOverlay')
    );
  }
}
